import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Menu } from "lucide-react";
import { logout } from "../../store";

import Logo from "../../assets/logo.svg";
import { ReactComponent as AdminIcon } from "../../assets/sideBar/admin-icon.svg";
import { ReactComponent as AddUserIcon } from "../../assets/sideBar/user-add-icon.svg";
import { ReactComponent as LogoutIcon } from "../../assets/sideBar/logout-icon.svg";

const topMenuItems = [
  {
    label: "Admin Panel",
    icon: AdminIcon,
    path: "/admin",
  },
  {
    label: "Create New User",
    icon: AddUserIcon,
    path: "/admin/create-user?mode=create",
  },
];

const bottomMenuItems = [
  {
    label: "Logout",
    icon: LogoutIcon,
    path: "",
  },
];

function SideBarLink({ icon: Icon, label, active, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2 rounded-lg font-inter cursor-pointer transition-colors
        ${active ? "bg-[var(--background-dull-green)]" : ""}
        hover:bg-[var(--background-light-green)]
      `}
    >
      {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5" />}
      <span className="text-lg whitespace-nowrap">{label}</span>
    </div>
  );
}

const isActive = (menuPath, currentPath) => {
  const getPathAndMode = (path) => {
    const [pathname, query] = path.split("?");
    const params = new URLSearchParams(query);
    return { pathname: pathname.replace(/\/+$/, ""), mode: params.get("mode") };
  };

  const { pathname: menuPathname, mode: menuMode } = getPathAndMode(menuPath);
  const { pathname: currentPathname, mode: currentMode } =
    getPathAndMode(currentPath);

  if (menuPathname === "") return false;

  return (
    menuPathname === currentPathname && (!menuMode || menuMode === currentMode)
  );
};

const AdminSideBar = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <>
      {/* Hamburger for mobile */}
      <div className="fixed z-40 top-4 md:ml-[20px] lg:ml-[50px]">
        <button
          className="p-2 focus:outline-none"
          onClick={() => setOpen(true)}
          aria-label="Open sidebar"
        >
          <Menu size={28} />
        </button>
      </div>

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-30"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed z-40 top-0 left-0 h-screen bg-white flex flex-col justify-between
          w-[330px] transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Top Section */}
        <div className="flex flex-col items-start flex-grow w-full max-h-screen px-6 pt-8 overflow-y-auto ">
          {/* Mobile Close */}
          <div className="flex items-center justify-between w-full mb-0 relative top-[-14px] lg:hidden">
            <img src={Logo} alt="Roselea Logo" className="w-[235px] h-[64px]" />
          </div>

          {/* Desktop Logo */}
          <div className="hidden lg:flex flex-col items-center w-full mb-4 -mt-4">
            <img
              src={Logo}
              alt="LandLod Logo"
              className="w-[235px] h-[100px]"
            />
          </div>

          {/* Top Menu Items */}
          <div className="flex flex-col w-full gap-2 text-lg mt-2">
            {topMenuItems.map((item) => {
              let active = false;

              if (item.label === "Admin Panel") {
                active =
                  location.pathname === "/admin" ||
                  location.pathname === "/admin/";
              } else {
                active = isActive(
                  item.path,
                  location.pathname + location.search
                );
              }

              return (
                <SideBarLink
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  active={active}
                  onClick={() => handleNav(item.path)}
                />
              );
            })}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col px-6 py-4 gap-2">
          {bottomMenuItems.map((item) => (
            <SideBarLink
              key={item.label}
              icon={item.icon}
              label={item.label}
              active={false}
              onClick={() =>
                item.label === "Logout" ? handleLogout() : handleNav(item.path)
              }
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default AdminSideBar;
